/**
 * Banner components for informational strips and notification areas:
 * `<TopBanner>`, `<Banner>`, `<ClickableBanner>`.
 *
 * `<TopBanner>` is a full-width strip with an optional right-side icon slot.
 * `<Banner>` is a rounded box with a configurable background color.
 * `<ClickableBanner>` wraps `<Banner>` in a `<HoverView>` and derives
 * hover/press colors automatically via `toHoverColor`/`toPressColor`; pass
 * `onPress={null}` for a non-interactive fallback.
 */

import { toHoverColor, toPressColor, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { StyleSheet, View } from 'react-native';

import { HorizBox, HoverView } from './basics';
import { spacings } from './constants';

export type TopBannerProps = { rightIcon?: React.ReactNode; children: React.ReactNode };

export function TopBanner({ rightIcon, children }: TopBannerProps) {
    const s = useTopBannerStyles();
    return (
        <View style={s.topBanner}>
            <View style={s.body}>{children}</View>
            {rightIcon && <View style={s.rightIcon}>{rightIcon}</View>}
        </View>
    );
}
const useTopBannerStyles = createThemedStyles((tokens) => ({
    topBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: tokens.colors.colorSurfaceHighlight,
        paddingHorizontal: 20,
        paddingVertical: 15,
    },
    body: {
        flex: 1,
    },
    rightIcon: {
        marginLeft: 10,
    },
}));

export type BannerProps = { color?: string; children: React.ReactNode };

export function Banner({ color, children }: BannerProps) {
    const { colors } = useTheme();
    const backgroundColor = color ?? colors.colorSurfaceBannerWarning;
    return <View style={[BannerStyle.banner, { backgroundColor }]}>{children}</View>;
}
const BannerStyle = StyleSheet.create({
    banner: {
        paddingHorizontal: spacings.lg,
        paddingVertical: spacings.md,
        borderRadius: 8,
        overflow: 'hidden',
    },
});

export type ClickableBannerProps = {
    testID?: string;
    onPress?: (() => void) | null;
    color?: string;
    hoverColor?: string;
    pressColor?: string;
    children: React.ReactNode;
};

export function ClickableBanner({ testID, onPress, color, hoverColor, pressColor, children }: ClickableBannerProps) {
    const { colors } = useTheme();
    const defaultColor = colors.colorSurfaceBannerWarning;
    const resolvedColor = color ?? defaultColor;
    const resolvedHoverColor = hoverColor ?? toHoverColor(defaultColor);
    const resolvedPressColor = pressColor ?? toPressColor(defaultColor);
    return (
        <HoverView
            hoverStyle={{ backgroundColor: resolvedHoverColor }}
            onPress={onPress}
            pressedStyle={{ backgroundColor: resolvedPressColor }}
            style={[ClickableBannerStyle.banner, { backgroundColor: resolvedColor }]}
            testID={testID}
        >
            <HorizBox center spread>
                {children}
            </HorizBox>
        </HoverView>
    );
}
const ClickableBannerStyle = StyleSheet.create({
    banner: {
        paddingHorizontal: 20,
        paddingVertical: 15,
        borderRadius: 8,
    },
});
